import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { User, UserRole } from "../data/schemaTypes";
import { setApiUserId } from "../data/api";
import { useMastersStore } from "../data/mastersStore";
import { validateCredentials } from "./validateCredentials";

// AuthContext owns the "who is logged in right now" question. The
// implementation is fully client-side until the backend lands (Fase 3):
// the user object is fetched from the masters store on login and cached
// in localStorage so the session survives a refresh. When API-3 ships,
// validateCredentials() will return a JWT plus user payload and we add
// a token field here — the consumers (ProtectedRoute / RoleGuard / hooks)
// don't change.

const STORAGE_KEY = "micelio-auth";
const STORAGE_VERSION = 1;

type AuthState = {
  currentUser: User | null;
  loading: boolean;
};

type AuthContextValue = AuthState & {
  /** Returns null on success, an error string on failure. */
  login: (email: string, password: string) => Promise<string | null>;
  logout: () => void;
  hasRole: (...roles: UserRole[]) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function loadPersisted(): User | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { v: number; user: User };
    if (parsed.v !== STORAGE_VERSION) return null;
    return parsed.user;
  } catch {
    return null;
  }
}

function persist(user: User | null) {
  if (user) {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ v: STORAGE_VERSION, user }),
    );
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const u = loadPersisted();
    // Mantenemos el cliente HTTP sincronizado desde el primer render —
    // si hay sesión persistida, /api/* ya manda X-User-ID en su primera
    // request (sino la página carga sin scope y un supervisor vería
    // datos que no le corresponden por un instante).
    setApiUserId(u?.id ?? null);
    return u;
  });
  const [loading, setLoading] = useState(false);

  // Re-sincronizar el header con cada cambio de sesión.
  useEffect(() => {
    setApiUserId(currentUser?.id ?? null);
  }, [currentUser]);

  // Keep storage in sync if another tab logs out: the cross-tab listener
  // updates this tab's state. Avoids a stale "logged in" UI after the
  // user clicks logout in tab A and switches to tab B.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setCurrentUser(loadPersisted());
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<string | null> => {
      setLoading(true);
      try {
        const r = await validateCredentials(email, password);
        if (!r.ok) return r.reason;
        setCurrentUser(r.user);
        persist(r.user);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const logout = useCallback(() => {
    // Orden importa: 1) limpiar localStorage del auth, 2) limpiar el
    // resto de stores. Si el orden se invierte y la UI re-renderiza
    // entre pasos, podríamos ver un flash de la lista vacía mientras
    // todavía hay sesión (porque mastersStore se limpió pero el
    // AuthContext aún ve currentUser). Hacerlo en este orden minimiza
    // el flicker — al limpiar mastersStore ya estamos navegando.
    setCurrentUser(null);
    persist(null);
    // Limpieza cross-store: cualquier dato del user que se va se
    // descarta para no leakear entre sesiones.
    useMastersStore.getState().clearForLogout();
  }, []);

  const hasRole = useCallback(
    (...roles: UserRole[]) =>
      currentUser != null && roles.includes(currentUser.role),
    [currentUser],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ currentUser, loading, login, logout, hasRole }),
    [currentUser, loading, login, logout, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

// Default landing for each role. Used after login and as fallback when a
// role-guarded route rejects the user — we don't want to leave them
// stranded on a redirect; send them somewhere they're allowed.
export function defaultRouteFor(role: UserRole): string {
  switch (role) {
    case "admin":
      // Admin arranca en la vista operativa global. Desde el sidebar
      // puede saltar a programación, coordinador, supervisor.
      return "/tracking";
    case "coordinator":
      // Coordinador supervisa N supervisores → arranca en su dashboard
      // de equipo. Drill-down a un supervisor abre /supervisor?supervisor=.
      return "/coordinador";
    case "supervisor":
      // Supervisor arranca en su dashboard de avance del día (su flota).
      return "/supervisor";
    case "driver":
      // Drivers don't have a web home yet — the mobile app is their
      // surface. Until MOB-1, kicking them back to /login with a message
      // is the least surprising thing.
      return "/login?msg=use-mobile-app";
  }
}
